import React, { useState, useRef } from 'react';
import {
  Paper,
  Box,
  Stack,
  Typography,
  Menu,
  MenuItem,
  ButtonBase,
  Badge,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { feedbackHeroTokens } from './feedbackHeroTokens';

const FilterPill = ({
  icon: Icon,
  label,
  value,
  options,
  onChange,
  defaultValue,
  accentTone,
}) => {
  const { fonts, colors } = feedbackHeroTokens;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const isActive = value !== defaultValue;
  const accent = accentTone ? colors[accentTone] : null;
  const currentOption = options.find((o) => String(o.value) === String(value)) || options[0];

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen(true)}
        focusRipple
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.85,
          borderRadius: 1.5,
          maxWidth: '100%',
          minWidth: 0,
          background: isActive
            ? `${accent ? accent.bg : colors.surfaceAlt}`
            : colors.surface,
          border: `1px solid ${isActive ? (accent ? accent.chip : colors.border) : colors.softBorder}`,
          boxShadow: isActive
            ? `0 4px 12px ${(accent ? accent.accent : colors.text)}14`
            : '0 2px 6px rgba(15, 23, 42, 0.04)',
          transition: 'all .2s ease',
          fontFamily: fonts.body,
          '&:hover': {
            borderColor: isActive ? (accent ? accent.accent : colors.text) : colors.border,
            background: isActive
              ? (accent ? `${accent.bg}DD` : colors.surfaceAlt)
              : colors.surfaceAlt,
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Icon
          sx={{
            fontSize: 16,
            color: isActive ? (accent ? accent.fg : colors.text) : colors.subtext,
          }}
        />
        <Typography
          sx={{
            fontFamily: fonts.body,
            fontWeight: 700,
            fontSize: '0.72rem',
            color: colors.subtext,
            textTransform: 'uppercase',
            letterSpacing: '.04em',
            flex: '0 0 auto',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: fonts.body,
            fontWeight: 700,
            fontSize: '0.82rem',
            color: isActive ? (accent ? accent.fg : colors.text) : colors.text,
            ml: 0.25,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {currentOption.label}
        </Typography>
        <KeyboardArrowDownRoundedIcon
          sx={{
            fontSize: 16,
            color: colors.subtext,
            transition: 'transform .2s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
          }}
        />
      </ButtonBase>

      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              borderRadius: 1.5,
              border: `1px solid ${colors.softBorder}`,
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
              minWidth: 180,
            },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {options.map((opt) => {
          const selected = String(opt.value) === String(value);
          return (
            <MenuItem
              key={String(opt.value)}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              sx={{
                fontFamily: fonts.body,
                fontSize: '0.85rem',
                fontWeight: selected ? 700 : 500,
                color: selected ? (accent ? accent.fg : colors.text) : colors.text,
                background: selected ? `${(accent ? accent.bg : colors.surfaceAlt)}88` : 'transparent',
                gap: 1,
                '&:hover': {
                  background: `${(accent ? accent.bg : colors.surfaceAlt)}55`,
                },
              }}
            >
              <Box sx={{ flex: 1 }}>{opt.label}</Box>
              {selected && (
                <CheckRoundedIcon
                  sx={{ fontSize: 16, color: accent ? accent.accent : colors.text }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export const DEFAULT_FEEDBACK_PERIOD = 'last15';

const periodOptions = [
  { value: 'all', label: 'Todo período' },
  { value: 'today', label: 'Hoje' },
  { value: 'last7', label: 'Últimos 7 dias' },
  { value: 'last15', label: 'Últimos 15 dias' },
  { value: 'month', label: 'Este mês' },
];

const sentimentoOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'pos', label: 'Positivos' },
  { value: 'neu', label: 'Neutros' },
  { value: 'neg', label: 'Negativos' },
];

const notaOptions = [
  { value: 'all', label: 'Todas' },
  { value: 5, label: '5 estrelas' },
  { value: 4, label: '4 estrelas' },
  { value: 3, label: '3 estrelas' },
  { value: 2, label: '2 estrelas' },
  { value: 1, label: '1 estrela' },
];

const origemOptions = [
  { value: 'all', label: 'Todos os canais' },
  { value: 'formulario', label: 'Formulário' },
  { value: 'atendabot', label: 'Atendabot' },
  { value: 'reservo', label: 'Reservo' },
  { value: 'ifood', label: 'iFood' },
  { value: 'google', label: 'Google' },
];

const ordemOptions = [
  { value: 'desc', label: 'Mais recentes' },
  { value: 'asc', label: 'Mais antigos' },
];

const FeedbackFilters = ({
  filters,
  ordem,
  onFiltersChange,
  onOrderChange,
  onClearFilters,
}) => {
  const { fonts, colors } = feedbackHeroTokens;

  const activeCount = [
    filters.periodo !== DEFAULT_FEEDBACK_PERIOD,
    filters.sentimento !== 'all',
    filters.nota !== 'all',
    filters.origem !== 'all',
  ].filter(Boolean).length;

  const accentForSentimento =
    filters.sentimento === 'pos' ? 'pos' :
    filters.sentimento === 'neu' ? 'neu' :
    filters.sentimento === 'neg' ? 'neg' : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          background: `${colors.surface}EE`,
          backdropFilter: 'blur(8px)',
          border: `1px solid ${colors.softBorder}`,
          boxShadow: '0 6px 20px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ width: '100%' }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              pl: 0.75,
              pr: 1,
              borderRight: { md: `1px solid ${colors.softBorder}` },
              mr: { md: 0.5 },
            }}
          >
            <TuneRoundedIcon sx={{ fontSize: 18, color: colors.text }} />
            <Typography
              sx={{
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: '0.78rem',
                color: colors.text,
                textTransform: 'uppercase',
                letterSpacing: '.06em',
                display: { xs: 'none', sm: 'inline' },
              }}
            >
              Filtros
            </Typography>
            {activeCount > 0 && (
              <Badge
                badgeContent={activeCount}
                sx={{
                  '& .MuiBadge-badge': {
                    background: colors.pos.accent,
                    color: '#fff',
                    fontFamily: fonts.body,
                    fontWeight: 800,
                    fontSize: '0.65rem',
                    minWidth: 18,
                    height: 18,
                    position: 'static',
                    transform: 'none',
                  },
                }}
              />
            )}
            {activeCount > 0 && (
              <Tooltip title="Limpar todos os filtros" arrow>
                <ButtonBase
                  onClick={onClearFilters}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.35,
                    px: 0.85,
                    py: 0.4,
                    ml: 0.25,
                    borderRadius: 1,
                    color: colors.subtext,
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    transition: 'color .2s ease, background .2s ease',
                    '&:hover': {
                      background: colors.neg.bg,
                      color: colors.neg.fg,
                    },
                  }}
                >
                  <RestartAltRoundedIcon sx={{ fontSize: 14 }} />
                  Limpar
                </ButtonBase>
              </Tooltip>
            )}
          </Stack>

          <FilterPill
            icon={CalendarTodayOutlinedIcon}
            label="Período"
            value={filters.periodo}
            defaultValue={DEFAULT_FEEDBACK_PERIOD}
            options={periodOptions}
            onChange={(v) => onFiltersChange({ periodo: v })}
          />
          <FilterPill
            icon={SentimentSatisfiedAltIcon}
            label="Sentimento"
            value={filters.sentimento}
            defaultValue="all"
            options={sentimentoOptions}
            accentTone={accentForSentimento}
            onChange={(v) => onFiltersChange({ sentimento: v })}
          />
          <FilterPill
            icon={StarBorderRoundedIcon}
            label="Nota"
            value={filters.nota}
            defaultValue="all"
            options={notaOptions}
            onChange={(v) => onFiltersChange({ nota: v })}
          />
          <FilterPill
            icon={HubOutlinedIcon}
            label="Canal"
            value={filters.origem ?? 'all'}
            defaultValue="all"
            options={origemOptions}
            onChange={(v) => onFiltersChange({ origem: v })}
          />

          <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', md: 'block' } }} />

          <FilterPill
            icon={SortRoundedIcon}
            label="Ordenar"
            value={ordem}
            defaultValue="desc"
            options={ordemOptions}
            onChange={(v) => onOrderChange(v)}
          />
        </Stack>
      </Paper>
    </motion.div>
  );
};

export default FeedbackFilters;
